import { Search } from '@icon-park/react';
import { noop } from 'lodash';
import {
  type ForwardedRef,
  forwardRef,
  type MemoExoticComponent,
  memo,
  useCallback,
  useEffect,
} from 'react';
import { useIntl } from 'react-intl';

import { useForwardedRef } from '@didaskill/ui/utils';

import { Input, type InputProps } from '../input/Input';
import messages from './SearchInput.messages';

export type SearchInputProps = Omit<InputProps, 'onChange' | 'onBlur' | 'iconRight'> & {
  onChange?: (value: string) => void;
  onCancelSearch?: () => void;
};

export const SearchInputBase = forwardRef(
  (
    {
      value = null,
      onChange = noop,
      onCancelSearch = noop,
      placeholder = null,
      ...props
    }: SearchInputProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    const intl = useIntl();

    const inputRef = useForwardedRef(ref);

    useEffect(() => {
      inputRef.current.focus();
    }, [inputRef]);

    const onBlur = useCallback(() => {
      if (!value) {
        onCancelSearch();
      }
    }, [value, onCancelSearch]);

    return (
      <Input
        {...props}
        ref={inputRef}
        value={value}
        onChange={onChange}
        iconRight={<Search />}
        placeholder={placeholder || intl.formatMessage(messages.DEFAULT_SEARCH_PLACEHOLDER)}
        onBlur={onBlur}
      />
    );
  },
);

SearchInputBase.displayName = 'SearchInput';

export const SearchInput: MemoExoticComponent<typeof SearchInputBase> & {
  SIZES?: typeof Input.SIZES;
  MAX_LENGTH_SIZE_MAPPING?: typeof Input.MAX_LENGTH_SIZE_MAPPING;
} = memo(SearchInputBase);

SearchInput.SIZES = Input.SIZES;
SearchInput.MAX_LENGTH_SIZE_MAPPING = Input.MAX_LENGTH_SIZE_MAPPING;
